/**
 * timelineBuilder — pure function that turns a `Case[]` into a chronological
 * `TimelinePoint[]` for the Chart.js timeline view.
 *
 * Duplicated from `packages/server/src/domain/timelineBuilder.ts` for the
 * same reason as `indexCalculator.ts` — `@aim/server` boots a Fastify
 * process on import and is not browser-safe. Behaviour matches the server
 * copy field-for-field and is covered by the tests in that package.
 *
 * Guarantees (Requirement 11):
 *  - Empty input returns `[]`.
 *  - Output length equals input length.
 *  - Output is stable-sorted by `dateISO` ascending (ties preserve input order).
 *  - `points[0].day === 0`; `points[i].day` is monotonically non-decreasing.
 *  - `points[i].total === i + 1` and `prod + dev + pipe === total`.
 *  - `points[i].maturity` is monotonically non-decreasing.
 *  - `points[i].revenue|adoption|tasks` are cumulative sums over `sorted[0..i]`.
 *  - Never mutates the input array or any nested `Case` object.
 *
 * Unlike the server copy, this browser-side builder is intentionally
 * lenient: cases with missing or non-parseable `dateISO` sort **last** and
 * anchor at `day === 0` for the purposes of the cumulative point. This
 * keeps the UI resilient to malformed inputs (e.g. a snapshot produced by
 * an older build) rather than throwing on render.
 */

import type { Case, TimelinePoint } from "@aim/types";

const MS_PER_DAY = 86_400_000;

function toEpochMsOrNull(dateISO: string | null | undefined): number | null {
  if (typeof dateISO !== "string" || dateISO.length === 0) return null;
  const t = Date.parse(dateISO);
  return Number.isFinite(t) ? t : null;
}

function numOrZero(v: number | null | undefined): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

/**
 * Build the cumulative timeline for a portfolio.
 *
 * The function stable-sorts a shallow copy by `dateISO` ascending, computes
 * an epoch-day origin from the earliest parseable case, and streams a
 * single pass to emit the timeline. Cases with unparseable `dateISO` land
 * at the end of the sort order, keyed to `day === 0` if they precede any
 * valid dates.
 */
export function buildTimeline(cases: readonly Case[]): TimelinePoint[] {
  if (cases.length === 0) return [];

  // Stable sort by dateISO ascending. Non-parseable dates sort last.
  const sorted = [...cases].sort((a, b) => {
    const ta = toEpochMsOrNull(a.dateISO);
    const tb = toEpochMsOrNull(b.dateISO);
    if (ta === null && tb === null) return 0;
    if (ta === null) return 1;
    if (tb === null) return -1;
    return ta - tb;
  });

  // Epoch-day origin. Fall back to 0 when no case has a parseable date.
  const firstMs = toEpochMsOrNull(sorted[0]?.dateISO);
  const day0 = firstMs !== null ? Math.floor(firstMs / MS_PER_DAY) : 0;

  let prod = 0;
  let dev = 0;
  let pipe = 0;
  let revenue = 0;
  let adoption = 0;
  let tasks = 0;

  const points: TimelinePoint[] = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const c = sorted[i]!;

    if (c.status === "Production") prod += 1;
    else if (c.status === "Development") dev += 1;
    else pipe += 1;

    revenue += numOrZero(c.revenue);
    adoption += numOrZero(c.adoption);
    tasks += numOrZero(c.tasks);

    const ms = toEpochMsOrNull(c.dateISO);
    const day = ms !== null ? Math.floor(ms / MS_PER_DAY) - day0 : 0;

    points.push({
      day,
      dateISO: c.dateISO,
      caseId: c.id,
      maturity: prod,
      total: i + 1,
      prod,
      dev,
      pipe,
      revenue,
      adoption,
      tasks,
    });
  }

  return points;
}
